import { useTenantSettings } from '@/features/company/queries'
import { useAuth } from '@/auth/useAuth'
import { useProfile } from '@/features/profile/queries'
import { getUserLocale } from '@/lib/locale'

/**
 * Header information stamped onto every branded PDF export. Resolved once
 * at click time and passed into the export function so the PDF builder
 * never reaches into React state.
 */
export type ReportBrand = {
  businessName: string
  legalEntity?: string | null
  logoUrl?: string | null
  addressLines: string[]
  email?: string | null
  phone?: string | null
  country?: string | null
  currency: string
  locale: string
  preparedByName: string
  preparedByEmail: string
}

/**
 * React hook that assembles the brand block from tenant settings + the
 * signed-in user. Falls back to safe defaults so a half-configured tenant
 * still produces a valid header (no "undefined" strings in PDFs).
 */
export function useReportBrand(): ReportBrand {
  const { user } = useAuth()
  const tenantQ = useTenantSettings()
  const profileQ = useProfile()
  const locale = getUserLocale()

  const t = tenantQ.data?.tenant ?? {}
  const businessName = (t.business_name || t.name || 'Bokapsys').trim()
  // Country is suppressed unless there's a real address to anchor it to;
  // otherwise a country-only tenant prints just "GB" under the company
  // name, which reads as either a typo or duplicated currency info.
  const street = [t.address_line1, t.address_line2].filter(Boolean).join(', ')
  const cityRow = [t.city, t.state_region, t.postal_code].filter(Boolean).join(', ')
  const hasRealAddress = !!(street || cityRow)
  const countrySuffix = hasRealAddress && t.country ? t.country : ''
  const addressLines = [
    street,
    [cityRow, countrySuffix].filter(Boolean).join(', '),
  ].filter(Boolean) as string[]

  const preparedByName =
    profileQ.data?.name?.trim() ||
    user?.email?.split('@')[0] ||
    'Bokapsys user'
  const preparedByEmail = profileQ.data?.email || user?.email || ''

  return {
    businessName,
    legalEntity: null,
    logoUrl: t.logo_url ?? null,
    addressLines,
    email: t.business_email ?? null,
    phone: t.business_phone ?? null,
    country: t.country ?? null,
    currency: locale.currency,
    locale: locale.locale,
    preparedByName,
    preparedByEmail,
  }
}
